import React, { useEffect, useState } from "react";
import { UserFeedbackSubmission } from "src/types/userFeedback";
import { ValidationErrors } from "src/errors";
import { FormLvlErrorMessage } from "src/components/ui/feedback";
import surveyConfig from "src/constants/surveyConfig.json";

interface FeedbackModalProps {
  isOpen: boolean;
  onSubmit: (feedbackData: UserFeedbackSubmission) => void;
  isLoading?: boolean;
  validationErrors?: ValidationErrors<keyof UserFeedbackSubmission>;
  formLvlErrorMessage?: string;
}

interface Option {
  value: string;
  label: string;
  description: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onSubmit,
  isLoading = false,
  validationErrors,
  formLvlErrorMessage
}) => {
  const [formData, setFormData] = useState<Partial<UserFeedbackSubmission>>({
    improvements: "",
    additionalComments: ""
  });

  const [showValidation, setShowValidation] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Reset form when modal opens
      setFormData({
        improvements: "",
        additionalComments: ""
      });
      setShowValidation(false);
      setStartTime(null); // Reset timer when modal opens
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleInputChange = (field: keyof UserFeedbackSubmission, value: any) => {
    // Start timer on first question answered (if not already started)
    if (startTime === null && value && value !== "") {
      setStartTime(Date.now());
    }

    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Clear ediPlatform if ediConnectionSupport changes to something other than "yes"
      if (field === "ediConnectionSupport" && value !== "yes") {
        delete updated.ediPlatform;
      }

      return updated;
    });
  };

  const validateForm = (): boolean => {
    const requiredFields = surveyConfig.questions.filter((q) => q.required).map((q) => q.id);
    for (const field of requiredFields) {
      if (!formData[field as keyof UserFeedbackSubmission]) return false;
    }

    // Conditional validation: ediPlatform is required when ediConnectionSupport is "yes"
    if (formData.ediConnectionSupport === "yes" && !formData.ediPlatform?.trim()) {
      return false;
    }

    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    if (validateForm()) {
      // Clean up form data: remove ediPlatform if ediConnectionSupport is not "yes"
      const cleanedData = { ...formData };
      if (cleanedData.ediConnectionSupport !== "yes") {
        delete cleanedData.ediPlatform;
      }

      // Calculate completion time in seconds
      if (startTime !== null) {
        const endTime = Date.now();
        const completionTimeSeconds = Math.round((endTime - startTime) / 1000);
        cleanedData.completionTimeSeconds = completionTimeSeconds;
      }

      onSubmit(cleanedData as UserFeedbackSubmission);
    }
  };

  const getFormErrors = (): number => {
    const requiredFields = surveyConfig.questions.filter((q) => q.required).map((q) => q.id);
    let errorCount = 0;
    for (const field of requiredFields) {
      if (!formData[field as keyof UserFeedbackSubmission]) errorCount++;
    }

    // Conditional validation: ediPlatform is required when ediConnectionSupport is "yes"
    if (formData.ediConnectionSupport === "yes" && !formData.ediPlatform?.trim()) {
      errorCount++;
    }

    return errorCount;
  };

  const getFieldError = (questionId: string): string | undefined => {
    if (showValidation) {
      const question = surveyConfig.questions.find((q) => q.id === questionId);
      const value = formData[questionId as keyof UserFeedbackSubmission];

      // Check if field is required
      if (question?.required && !value) {
        return question.type === "radio" ? "Please select an option" : "This field is required";
      }

      // Conditional validation: ediPlatform is required when ediConnectionSupport is "yes"
      if (
        questionId === "ediPlatform" &&
        formData.ediConnectionSupport === "yes" &&
        (!value || (typeof value === "string" && !value.trim()))
      ) {
        return "This field is required when EDI connection support is 'Yes'";
      }
    }
    return validationErrors?.getErrorMessageByFormDataKey(questionId as keyof UserFeedbackSubmission);
  };

  const shouldShowQuestion = (question: any): boolean => {
    // Check if question has conditional rendering
    if (question.conditionalOn) {
      const conditionalValue = formData[question.conditionalOn.field as keyof UserFeedbackSubmission];
      return conditionalValue === question.conditionalOn.value;
    }
    return true;
  };

  const renderQuestion = (question: any, questionNumber: number) => {
    const error = getFieldError(question.id);
    const hasError = Boolean(error);
    const value = formData[question.id as keyof UserFeedbackSubmission];

    if (question.type === "radio") {
      return (
        <div
          key={question.id}
          className="bg-white/90 backdrop-blur-md rounded-xl shadow-sm border border-primary-200 p-6 mb-6"
        >
          <div className="mb-4">
            <h3 className="text-lg font-medium text-primary-900">
              {questionNumber}. {question.question}
              {question.required && <span className="text-error-500 ml-1">*</span>}
            </h3>
            {question.category && <p className="text-sm text-primary-500">{question.category}</p>}
          </div>

          <div className="space-y-2">
            {question.options?.map((option: Option) => (
              <label
                key={option.value}
                className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:bg-primary-100 ${
                  value === option.value
                    ? "border-primary-500 bg-primary-100"
                    : hasError
                      ? "border-error-300"
                      : "border-primary-50"
                }`}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleInputChange(question.id as keyof UserFeedbackSubmission, e.target.value)}
                  className="mt-1 h-4 w-4 text-primary-600 border-primary-300 focus:ring-primary-500"
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-primary-900">{option.label}</div>
                  {option.description && <div className="text-xs text-primary-500">{option.description}</div>}
                </div>
              </label>
            ))}
          </div>

          {error && <p className="mt-2 text-sm text-error-600">{error}</p>}
        </div>
      );
    }

    if (question.type === "textarea") {
      // Check if this field is conditionally required
      const isConditionallyRequired =
        question.conditionalOn &&
        formData[question.conditionalOn.field as keyof UserFeedbackSubmission] === question.conditionalOn.value;
      const isRequired = question.required || isConditionallyRequired;

      return (
        <div
          key={question.id}
          className="bg-white/90 backdrop-blur-md rounded-xl shadow-sm border border-primary-200 p-6 mb-6"
        >
          <label htmlFor={question.id} className="block text-lg font-medium text-primary-900 mb-2">
            {questionNumber}. {question.question}
            {isRequired && <span className="text-error-500 ml-1">*</span>}
          </label>
          {question.category && <p className="text-sm text-primary-500 mb-2">{question.category}</p>}
          <textarea
            id={question.id}
            value={value || ""}
            onChange={(e) => handleInputChange(question.id as keyof UserFeedbackSubmission, e.target.value)}
            placeholder={question.placeholder}
            maxLength={question.maxLength}
            rows={question.rows || 4}
            className={`w-full px-3 py-2 border rounded-lg shadow-sm placeholder-primary-400 text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition box-border ${
              hasError ? "border-error-300" : "border-primary-300"
            }`}
          />
          {question.maxLength && (
            <div className="mt-1 text-xs text-primary-500 text-right">
              {typeof value === "string" ? value.length : 0} / {question.maxLength}
            </div>
          )}
          {error && <p className="mt-2 text-sm text-error-600">{error}</p>}
        </div>
      );
    }

    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="relative bg-gradient-to-b from-white to-primary-50 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-slideUp">
        {/* Header */}
        <div className="px-6 py-5 border-b border-primary-200 bg-white/80 backdrop-blur-sm flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-primary-900">{surveyConfig.title}</h1>
            <p className="text-sm text-primary-600">{surveyConfig.description}</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto px-6 py-6">
          {surveyConfig.questions
            .filter((question) => shouldShowQuestion(question))
            .map((question, visibleIndex) => renderQuestion(question, visibleIndex + 1))}

          <div className="flex justify-between items-center mt-8">
            {showValidation && !validateForm() ? (
              <div className="flex items-center text-error-600">
                <svg className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span className="text-sm font-medium">
                  Please complete all required fields ({getFormErrors()} missing)
                </span>
              </div>
            ) : (
              <div></div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="bg-primary-900 hover:bg-primary-800 disabled:bg-primary-400 text-white font-medium py-2.5 px-6 rounded-lg shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {surveyConfig.submitButton?.loadingText || "Submitting..."}
                </div>
              ) : (
                surveyConfig.submitButton?.text || "Submit Feedback"
              )}
            </button>
          </div>
          {formLvlErrorMessage && (
            <div className="mb-4">
              <FormLvlErrorMessage message={formLvlErrorMessage} />
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export { FeedbackModal };
